import React from 'react'
import { ChevronRight } from 'lucide-react'
import { NavLink, useLocation } from 'react-router-dom'
import { appLink } from '@app/config/appLink'
import { GuideMainTopics } from '@app/components/Guide/types/guide.types'
import { cn } from '@/lib/tailwind.utils'

export type GuideNavLink = { title: string; slug: string }
export type GuideNavSection = { title: string; links: GuideNavLink[] }

type GuideSubTopicNavigationProps = {
  navigation?: GuideNavSection[]
  onLinkClick?: React.MouseEventHandler<HTMLAnchorElement>
  handleClickTopic?: () => void
  allTopicsActive?: boolean
  className?: string
}

export const GuideSubTopicNavigation: React.FC<GuideSubTopicNavigationProps> = ({
  navigation = [],
  onLinkClick,
  handleClickTopic,
  allTopicsActive,
  className,
}) => {
  const { pathname } = useLocation()
  const isGlossary = pathname.includes(GuideMainTopics.GLOSSARY)

  return (
    <aside className={cn('relative hidden flex-none lg:block lg:w-72', className)}>
      <div className="bg-background absolute right-0 top-0 bottom-0 -z-10 w-1/2 dark:hidden" />

      <div className="sticky top-[4.75rem] ml-[-0.125rem] h-[calc(100vh-4.75rem)] w-72 overflow-x-hidden overflow-y-auto py-16 pl-0.5 pr-8 [scrollbar-color:rgba(0,0,0,0.2)_transparent] [scrollbar-width:thin] xl:pr-16 [&::-webkit-scrollbar]:w-3 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-md [&::-webkit-scrollbar-thumb]:border-[3px] [&::-webkit-scrollbar-thumb]:border-transparent [&::-webkit-scrollbar-thumb]:bg-[rgba(0,0,0,0.2)] [&::-webkit-scrollbar-thumb]:bg-clip-content hover:[&::-webkit-scrollbar-thumb]:bg-[rgba(0,0,0,0.3)] hover:[&::-webkit-scrollbar-thumb]:bg-clip-content">
        <nav className="text-base leading-6 lg:text-sm lg:leading-5">
          <ul role="list" className="m-0 list-none space-y-9 p-0">
            <li>
              <h2
                role="button"
                tabIndex={0}
                aria-pressed={Boolean(allTopicsActive)}
                onClick={handleClickTopic}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    handleClickTopic?.()
                  }
                }}
                className="flex cursor-pointer items-center pr-4 text-sm font-semibold leading-5 text-foreground"
              >
                {allTopicsActive ? 'View Current Topic' : 'Browse All Topics'}
                <ChevronRight className="h-5 w-5" />
              </h2>
            </li>

            {navigation.map((section) => (
              <li key={section.title}>
                <h2 className="flex cursor-pointer items-center pr-4 text-sm font-semibold leading-5 text-foreground">
                  {section.title}
                  <ChevronRight className="h-5 w-5" />
                </h2>

                <ul
                  role="list"
                  className="m-0 mt-2 list-none space-y-2 p-0 pl-0 text-sm lg:mt-4 dark:border-slate-800"
                >
                  {section.links.map((link) => (
                    <li key={link.slug} className="relative">
                      <NavLink
                        className="no-underline"
                        to={link.slug}
                        end={false}
                        onClick={onLinkClick}
                      >
                        {({ isActive }) => (
                          <span
                            className={cn(
                              'relative block w-full px-4 py-2 leading-[155%] text-foreground  transition-colors duration-100 ease-in-out',
                              isActive
                                ? 'rounded-2xl bg-[#D2E9FA] font-semibold text-foreground dark:text-black'
                                : 'hover:rounded-2xl hover:bg-[#D2E9FA] hover:text-foreground  dark:hover:text-black',
                            )}
                          >
                            {link.title}
                          </span>
                        )}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </li>
            ))}

            {allTopicsActive ||
              (isGlossary && (
                <li>
                  <h2 className="flex cursor-pointer items-center pr-4 text-sm font-semibold leading-5 text-foreground">
                    Glossary
                    <ChevronRight className="h-5 w-5" />
                  </h2>

                  <ul
                    role="list"
                    className="m-0 mt-2 list-none space-y-2 p-0 pl-0 text-sm lg:mt-4 dark:border-slate-800"
                  >
                    <li className="relative">
                      <NavLink
                        className="no-underline"
                        to={`${appLink.guidePage.path}/${GuideMainTopics.GLOSSARY}`}
                        end={false}
                        onClick={onLinkClick}
                      >
                        {({ isActive }) => (
                          <span
                            className={cn(
                              'relative block w-full px-4 py-2 leading-[155%] text-foreground transition-colors duration-100 ease-in-out',
                              isActive
                                ? 'rounded-2xl bg-[#D2E9FA] font-semibold text-foreground'
                                : 'hover:rounded-2xl hover:bg-[#D2E9FA] hover:text-foreground',
                            )}
                          >
                            {GuideMainTopics.GLOSSARY}
                          </span>
                        )}
                      </NavLink>
                    </li>
                  </ul>
                </li>
              ))}
          </ul>
        </nav>
      </div>
    </aside>
  )
}
